import * as Sentry from '@sentry/browser'
import { Resource } from '@opentelemetry/resources'
import { context, trace } from '@opentelemetry/api'
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions'
import {
	BatchSpanProcessor,
	ConsoleSpanExporter,
	ParentBasedSampler,
	SimpleSpanProcessor,
	TraceIdRatioBasedSampler,
	WebTracerProvider
} from '@opentelemetry/sdk-trace-web'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'

import config from '@/config'
import { browserEngine, browserName, browserVersion, os, version } from '@/helpers/device'
import { ENV_DEV_OR_STAGING, ENV_HACKERONE, ENV_LOCAL, VUE_APP_VERSION } from '@/helpers/environment'

const DEBUG = false
const SAMPLER_RATIO = 0.02
const SERVICE_NAME =
	ENV_DEV_OR_STAGING || ENV_HACKERONE ? 'simple-web-onboarding-development' : 'simple-web-onboarding-production'
const SERVICE_NAMESPACE = 'simple-web'

const COLLECTOR_OPTIONS = {
	url: 'https://otel-external.fstr.app/v1/traces'
}

const PROVIDER_CONFIG = {
	sampler: new ParentBasedSampler({
		root: new TraceIdRatioBasedSampler(SAMPLER_RATIO)
	}),
	resource: new Resource({
		[SemanticResourceAttributes.DEVICE_ID]: window.deviceId,
		[SemanticResourceAttributes.SERVICE_NAME]: SERVICE_NAME,
		[SemanticResourceAttributes.SERVICE_NAMESPACE]: SERVICE_NAMESPACE,
		[SemanticResourceAttributes.SERVICE_VERSION]: VUE_APP_VERSION,
		[SemanticResourceAttributes.OS_NAME]: os,
		[SemanticResourceAttributes.OS_VERSION]: version,
		'browser.name': browserName,
		'browser.engine': browserEngine,
		'browser.version': browserVersion
	})
}

/**
 * Class for interaction with client performance data.
 * @class
 */
class OpenTelemetryAPI {
	/**
	 * @param {object} options
	 * @param {import('@opentelemetry/sdk-trace-web').Tracer} options.tracer - opentelemetry tracer
	 * @param {number} options.startTime - timestamp from inlined <script> tag
	 * @constructor
	 */
	constructor(options) {
		this.tracer = options.tracer
		this.startTime = options.startTime ?? new Date().getTime()
		this.navigationObserver = null
		this.resourceObserver = null

		this.spans = {}
		this.contexts = {}

		const appStartSpan = this.startSpan('app-start', null, {
			startTime: this.startTime,
			attributes: {
				'span.type': 'app-start'
			}
		})
		this.contexts['app-start'] = trace.setSpan(context.active(), appStartSpan)

		this.createPerformanceObservers()
	}

	/**
	 * @param {string} spanId - ID of particular span
	 * @param {string} [contextId="app-start"] - ID of context ("app-start" by default)
	 * @param {import('@opentelemetry/api').SpanOptions} [options={}] - Options for span
	 * @returns {import('@opentelemetry/api').Span}
	 */
	startSpan(spanId, contextId = 'app-start', options = {}) {
		const context = this.contexts[contextId] ?? undefined
		const span = this.tracer.startSpan(spanId, options, context)

		this.spans[spanId] = span

		return span
	}

	/**
	 * @param {string} spanId - ID of particular span
	 * @returns {import('@opentelemetry/api').Span}
	 */
	getSpan(spanId) {
		if (!this.spans.hasOwnProperty(spanId)) {
			return
		}

		return this.spans[spanId]
	}

	/**
	 * @param {string} spanId - ID of particular span
	 * @param spanId
	 */
	endSpan(spanId) {
		const span = this.getSpan(spanId)

		if (!span || span.ended) {
			return
		}

		if (spanId === 'app-start') {
			this.beforeAppStartSpanEnd(spanId)
		}

		this.setSpanAttributes(spanId, {
			'location.hostname': window.location.hostname,
			'location.pathname': window.location.pathname
		})

		span.end()
	}

	/**
	 *
	 * @param {string} spanId - ID of particular span
	 * @param {import('@opentelemetry/api').SpanAttributes} attributes
	 */
	setSpanAttributes(spanId, attributes = {}) {
		if (!this.spans.hasOwnProperty(spanId)) {
			return
		}

		this.spans[spanId].setAttributes(attributes)
	}

	/**
	 * @param {string} spanId - ID of particular span
	 * @param {import('axios').AxiosResponse|import('axios').AxiosError} response
	 * @param {string} response.requestNormalizedUrl - normalized URL - host + pathname (w/o protocol, queryparams etc.)
	 */
	setNetworkRequestAttributes(spanId, response = {}) {
		try {
			const responseConfig = response.config ?? {}

			this.setSpanAttributes(spanId, {
				'span.type': 'network-request',
				'http.method': responseConfig.method?.toUpperCase?.(),
				'http.endpoint': response.requestNormalizedUrl,
				'http.retries': responseConfig['axios-retry']?.retryCount,
				'http.code': response.code ?? '',
				'http.status_code': response.status || response.response?.status,
				'http.status_text': response.statusText || response.response?.statusText
			})
		} catch (e) {
			Sentry.captureException(e)
		}
	}

	beforeAppStartSpanEnd(spanId) {
		const firstPaintMetrics = window.performance.getEntriesByName('first-paint')?.[0]
		const firstContentfulPaintMetrics = window.performance.getEntriesByName('first-contentful-paint')?.[0]
		const navigationMetrics = window.performance.getEntriesByType('navigation')?.[0]
		const startTime = this.startTime - navigationMetrics?.responseEnd

		this.setSpanAttributes(spanId, {
			'perf.duration': navigationMetrics?.duration,
			'perf.encoded_body_size': navigationMetrics?.encodedBodySize
		})

		this.startSpan('html-dns-lookup', 'app-start', {
			startTime: startTime,
			attributes: { 'span.type': 'html-metric' }
		}).end(startTime + navigationMetrics?.domainLookupEnd)
		this.startSpan('html-ttfb', 'app-start', {
			startTime: startTime,
			attributes: { 'span.type': 'html-metric' }
		}).end(startTime + navigationMetrics?.responseStart)
		this.startSpan('html-response', 'app-start', {
			startTime: startTime,
			attributes: { 'span.type': 'html-metric' }
		}).end(startTime + navigationMetrics?.responseEnd)
		this.startSpan('perf-dom-interactive', 'app-start', {
			startTime: startTime,
			attributes: { 'span.type': 'perf-metric' }
		}).end(startTime + navigationMetrics?.domInteractive)
		this.startSpan('perf-dom-complete', 'app-start', {
			startTime: startTime,
			attributes: { 'span.type': 'perf-metric' }
		}).end(startTime + navigationMetrics?.domComplete)

		if (firstPaintMetrics) {
			this.startSpan('perf-first-paint', 'app-start', {
				startTime: startTime,
				attributes: { 'span.type': 'perf-metric' }
			}).end(startTime + firstPaintMetrics?.startTime)
		}

		if (firstContentfulPaintMetrics) {
			this.startSpan('perf-first-contentful-paint', 'app-start', {
				startTime: startTime,
				attributes: { 'span.type': 'perf-metric' }
			}).end(startTime + firstContentfulPaintMetrics?.startTime)
		}

		if (ENV_DEV_OR_STAGING) {
			/* eslint-disable no-console */
			console.groupCollapsed('%cOpentelemetry [debug]', 'font-weight: 400')
			console.log(this.spans)
			console.groupEnd()
			/* eslint-enable no-console */
		}
	}

	createPerformanceObservers() {
		if (!window.PerformanceObserver) {
			window.ot.endSpan?.('app-start')
			return
		}

		const resourceHost = ENV_LOCAL ? window.location.origin : config('AssetsPublicUrl')
		const resourceNameRegExp = new RegExp(`${resourceHost}\\S*\/((js|css)\/[a-z][a-zA-Z0-9_-]+)\\.`)

		this.resourceObserver = new PerformanceObserver((list) => {
			for (const performanceEntry of list.getEntries()) {
				if (!['script', 'css', 'link'].includes(performanceEntry.initiatorType)) {
					continue
				}

				const resourseName = performanceEntry.name.match(resourceNameRegExp)?.[1]

				if (!resourseName) {
					continue
				}

				const startTime = this.startTime + performanceEntry.startTime

				this.startSpan(resourseName, 'app-start', {
					startTime: startTime,
					attributes: {
						initiator: performanceEntry.initiatorType,
						'span.type': 'resource',
						'http.status_code': performanceEntry.responseStatus,
						'http.endpoint': resourseName,
						'perf.duration': performanceEntry.duration,
						'perf.encoded_body_size': performanceEntry.encodedBodySize,
						'perf.decoded_body_size': performanceEntry.decodedBodySize,
						'perf.tcp_handshake': performanceEntry.connectEnd - performanceEntry.connectStart,
						'perf.dns_lookup': performanceEntry.domainLookupEnd - performanceEntry.domainLookupStart,
						'perf.request_time': performanceEntry.responseStart - performanceEntry.requestStart
					}
				}).end(startTime + performanceEntry.duration)
			}
		})
		this.resourceObserver.observe({ type: 'resource', buffered: true })

		this.navigationObserver = new PerformanceObserver((list) => {
			const performanceTiming = list.getEntries()[0] ?? {}

			if (performanceTiming.entryType === 'navigation') {
				window.ot.endSpan('app-start')

				this.navigationObserver?.disconnect()
				this.resourceObserver?.disconnect()
			}
		})
		this.navigationObserver.observe({ type: 'navigation' })
	}
}

export default {
	install() {
		const provider = new WebTracerProvider(PROVIDER_CONFIG)
		const exporter = new OTLPTraceExporter(COLLECTOR_OPTIONS)

		provider.register()

		if (DEBUG) {
			provider.addSpanProcessor(new SimpleSpanProcessor(new ConsoleSpanExporter()))
		}

		if (!ENV_LOCAL) {
			provider.addSpanProcessor(
				new BatchSpanProcessor(exporter, {
					maxExportBatchSize: 75,
					maxQueueSize: 128
				})
			)
		}

		const tracer = provider.getTracer('default')

		/** @type {OpenTelemetryAPI} */
		window.ot = new OpenTelemetryAPI({ tracer, startTime: window.ot?.startTime })
	}
}
